import { createServerFn } from "@tanstack/solid-start";
import { eq } from "drizzle-orm";
import { drizzle } from "drizzle-orm/node-postgres";
import { z } from "zod";
import { pool } from "~/lib/db";
import { withSentrySpan } from "~/lib/sentry-span";
import * as authSchema from "~/schemas/auth-schema";
import { user as userTable } from "~/schemas/auth-schema";
import { authFunctionMiddleware } from "~/server/middleware/auth";
import { reportUserStorageTotalUsage } from "~/server/stripe";
import { CreateDocumentSchema, UpdateDocumentSchema } from "./document.schema";

const authDb = drizzle(pool, { schema: authSchema });

export const DocumentIdInputSchema = z.object({
  id: z.string().trim().min(1),
  businessId: z.string().trim().min(1),
});

export const ListDocumentsInputSchema = z.object({
  businessId: z.string().trim().min(1),
  search: z.string().optional(),
  offset: z.number().int().min(0).optional(),
  limit: z.number().int().min(1).max(200).optional(),
  type: z.string().optional(),
  ids: z.array(z.string().trim().min(1)).max(500).optional(),
  /** Optional projection: return only these fields per document (whitelisted server-side). */
  fields: z.array(z.string().trim().min(1)).optional(),
  sortBy: z
    .enum([
      "name",
      "type",
      "date",
      "createdAt",
      "vendor",
      "amount",
      "fileSizeBytes",
    ])
    .optional(),
  sortDir: z.enum(["asc", "desc"]).optional(),
});

export const GetDocumentPostingContextInputSchema = z.object({
  businessId: z.string().trim().min(1),
  id: z.string().trim().min(1),
  /** Cap on related documents and related transactions returned (default 5). */
  limit: z.number().int().min(1).max(20).optional(),
});

export const CreateDocumentInputSchema = z.object({
  businessId: z.string().trim().min(1),
  item: CreateDocumentSchema,
});

export const UpdateDocumentInputSchema = z.object({
  ...DocumentIdInputSchema.shape,
  updates: UpdateDocumentSchema,
});

export const CheckUploadQuotaInputSchema = z.object({
  businessId: z.string().trim().min(1),
  incomingBytes: z.number().int().min(0),
});

export const PresignedUploadInputSchema = z.object({
  businessId: z.string().trim().min(1),
  fileName: z.string().trim().min(1),
  contentType: z.string().default("application/octet-stream"),
});

export const MultipartUploadInputSchema = z.object({
  businessId: z.string().trim().min(1),
  fileName: z.string().trim().min(1),
  contentType: z.string().default("application/octet-stream"),
  fileSizeBytes: z.number().int().min(1),
});

export const MultipartUploadFinalizeInputSchema = z.object({
  businessId: z.string().trim().min(1),
  fileKey: z.string().trim().min(1),
  uploadId: z.string().trim().min(1),
  parts: z
    .array(
      z.object({
        ETag: z.string().trim().min(1),
        PartNumber: z.number().int().min(1),
      }),
    )
    .min(1),
});

export const MultipartUploadAbortInputSchema = z.object({
  businessId: z.string().trim().min(1),
  fileKey: z.string().trim().min(1),
  uploadId: z.string().trim().min(1),
});

export const DeleteUploadedObjectInputSchema = z.object({
  businessId: z.string().trim().min(1),
  fileKey: z.string().trim().min(1),
});

export const DeleteDocumentsInputSchema = z.object({
  businessId: z.string().trim().min(1),
  ids: z.array(z.string().trim().min(1)).min(1).max(100),
});

export const listDocuments = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => ListDocumentsInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("documents.list", async () => {
      const { listDocumentsForUser } = await import("./document.server");
      return listDocumentsForUser(context.user.id, data.businessId, {
        search: data.search,
        offset: data.offset,
        limit: data.limit,
        type: data.type,
        ids: data.ids,
        sortBy: data.sortBy,
        sortDir: data.sortDir,
        fields: data.fields,
      });
    });
  });

export const getDocument = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => DocumentIdInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("documents.get", async () => {
      const { getDocumentForUser } = await import("./document.server");
      const result = await getDocumentForUser(
        context.user.id,
        data.businessId,
        data.id,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });

export const getDocumentRawText = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => DocumentIdInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("documents.getRawText", async () => {
      const { getDocumentRawTextForUser } = await import("./document.server");
      const result = await getDocumentRawTextForUser(
        context.user.id,
        data.businessId,
        data.id,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return {
        ...result,
        hasRawText: Boolean(result.ocrText?.trim()),
      };
    });
  });

export const getDocumentPostingContext = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => GetDocumentPostingContextInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("documents.getPostingContext", async () => {
      const { getDocumentPostingContextForUser } =
        await import("./document.server");
      const result = await getDocumentPostingContextForUser(
        context.user.id,
        data.businessId,
        data.id,
        { limit: data.limit },
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });

/**
 * Pre-flight the free-tier storage quota before an upload so the client can
 * prompt the user to upgrade instead of failing mid-upload. Best-effort — the
 * server re-checks in `createDocument` and remains the source of truth.
 */
export const checkUploadQuota = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => CheckUploadQuotaInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("documents.checkUploadQuota", async () => {
      const { checkDocumentUploadQuota } = await import("./document.server");
      return checkDocumentUploadQuota(context.user.id, data.incomingBytes);
    });
  });

export const createDocument = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => CreateDocumentInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("documents.create", async () => {
      const { createDocumentForUser } = await import("./document.server");
      const doc = await createDocumentForUser(
        context.user.id,
        data.businessId,
        data.item,
      );

      // Report total storage to Stripe meter (fire-and-forget)
      authDb.query.user
        .findFirst({ where: eq(userTable.id, context.user.id) })
        .then((dbUser) => {
          if (dbUser?.stripeCustomerId) {
            return reportUserStorageTotalUsage(
              dbUser.stripeCustomerId,
              context.user.id,
            );
          }
        })
        .catch((err) => console.error("Failed to report storage usage:", err));

      return doc;
    });
  });

export const updateDocument = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => UpdateDocumentInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("documents.update", async () => {
      const { updateDocumentForUser } = await import("./document.server");
      const result = await updateDocumentForUser(
        context.user.id,
        data.businessId,
        data.id,
        data.updates,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });

export const deleteDocument = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => DocumentIdInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("documents.delete", async () => {
      const { deleteDocumentForUser } = await import("./document.server");
      const result = await deleteDocumentForUser(
        context.user.id,
        data.businessId,
        data.id,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      // Clean up S3 object if one was stored
      if (result.fileKey) {
        const { deleteS3Object } = await import("~/server/s3");
        await deleteS3Object({ key: result.fileKey }).catch(() => {
          // Non-fatal: DB row is already deleted
        });
      }
      return result;
    });
  });

export const deleteDocuments = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => DeleteDocumentsInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("documents.deleteBatch", async () => {
      const { deleteDocumentForUser } = await import("./document.server");
      const { deleteS3Object } = await import("~/server/s3");

      const uniqueIds = Array.from(new Set(data.ids));
      const results: Array<{
        id: string;
        status: "deleted" | "not_found" | "error";
        error?: string;
      }> = [];

      for (const id of uniqueIds) {
        try {
          const result = await deleteDocumentForUser(
            context.user.id,
            data.businessId,
            id,
          );

          if (!result) {
            results.push({ id, status: "not_found" });
            continue;
          }

          if (result.fileKey) {
            await deleteS3Object({ key: result.fileKey }).catch(() => {
              // Non-fatal: DB row is already deleted.
            });
          }

          results.push({ id, status: "deleted" });
        } catch (error) {
          const message =
            error instanceof Error ? error.message : String(error);
          results.push({ id, status: "error", error: message });
        }
      }

      return {
        results,
        deletedCount: results.filter((r) => r.status === "deleted").length,
        notFoundCount: results.filter((r) => r.status === "not_found").length,
        errorCount: results.filter((r) => r.status === "error").length,
      };
    });
  });

export const getPresignedUploadUrl = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => PresignedUploadInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("documents.getPresignedUploadUrl", async () => {
      const { generateDocumentKey, createPresignedUploadUrl } =
        await import("~/server/s3");
      const fileKey = generateDocumentKey({
        userId: context.user.id,
        businessId: data.businessId,
        filename: data.fileName,
      });
      const presignedUrl = await createPresignedUploadUrl({
        key: fileKey,
        contentType: data.contentType,
      });
      return { presignedUrl, fileKey, expiresIn: 900 };
    });
  });

export const createMultipartUploadSession = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => MultipartUploadInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan(
      "documents.createMultipartUploadSession",
      async () => {
        const {
          generateDocumentKey,
          createMultipartUploadSession: createS3MultipartUploadSession,
        } = await import("~/server/s3");

        const fileKey = generateDocumentKey({
          userId: context.user.id,
          businessId: data.businessId,
          filename: data.fileName,
        });

        const session = await createS3MultipartUploadSession({
          key: fileKey,
          contentType: data.contentType,
          fileSizeBytes: data.fileSizeBytes,
        });

        return {
          fileKey,
          uploadId: session.uploadId,
          partSize: session.partSize,
          partCount: session.partCount,
          partUrls: session.partUrls,
          expiresIn: 900,
        };
      },
    );
  });

export const completeMultipartUpload = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => MultipartUploadFinalizeInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("documents.completeMultipartUpload", async () => {
      const expectedPrefix = `users/${context.user.id}/businesses/${data.businessId}/documents/`;
      if (!data.fileKey.startsWith(expectedPrefix)) {
        throw Response.json({ error: "Invalid file key" }, { status: 403 });
      }

      const { completeMultipartUpload: completeS3MultipartUpload } =
        await import("~/server/s3");
      await completeS3MultipartUpload({
        key: data.fileKey,
        uploadId: data.uploadId,
        parts: data.parts,
      });
      return { ok: true };
    });
  });

export const abortMultipartUpload = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => MultipartUploadAbortInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("documents.abortMultipartUpload", async () => {
      const expectedPrefix = `users/${context.user.id}/businesses/${data.businessId}/documents/`;
      if (!data.fileKey.startsWith(expectedPrefix)) {
        throw Response.json({ error: "Invalid file key" }, { status: 403 });
      }

      const { abortMultipartUpload: abortS3MultipartUpload } =
        await import("~/server/s3");
      await abortS3MultipartUpload({
        key: data.fileKey,
        uploadId: data.uploadId,
      }).catch(() => {
        // Non-fatal: the upload session may already be gone.
      });

      return { ok: true };
    });
  });

export const deleteUploadedObject = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => DeleteUploadedObjectInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("documents.deleteUploadedObject", async () => {
      const expectedPrefix = `users/${context.user.id}/businesses/${data.businessId}/documents/`;
      if (!data.fileKey.startsWith(expectedPrefix)) {
        throw Response.json({ error: "Invalid file key" }, { status: 403 });
      }

      const { deleteS3Object } = await import("~/server/s3");
      await deleteS3Object({ key: data.fileKey }).catch(() => {
        // Non-fatal: the object may not exist yet or may already be deleted.
      });

      return { ok: true };
    });
  });

export const getPresignedDownloadUrl = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => DocumentIdInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("documents.getPresignedDownloadUrl", async () => {
      const { getDocumentForUser } = await import("./document.server");
      const doc = await getDocumentForUser(
        context.user.id,
        data.businessId,
        data.id,
      );
      if (!doc || !doc.fileKey)
        throw Response.json({ error: "No file attached" }, { status: 404 });
      const { createPresignedDownloadUrl } = await import("~/server/s3");
      const presignedUrl = await createPresignedDownloadUrl({
        key: doc.fileKey,
      });
      return { presignedUrl, expiresIn: 300 };
    });
  });

export const getPresignedShareUrl = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => DocumentIdInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("documents.getPresignedShareUrl", async () => {
      const { getDocumentForUser } = await import("./document.server");
      const doc = await getDocumentForUser(
        context.user.id,
        data.businessId,
        data.id,
      );
      if (!doc || !doc.fileKey)
        throw Response.json({ error: "No file attached" }, { status: 404 });
      const { createPresignedDownloadUrl } = await import("~/server/s3");
      const presignedUrl = await createPresignedDownloadUrl({
        key: doc.fileKey,
        expiresIn: 3600,
      });
      return {
        presignedUrl,
        expiresIn: 3600,
        documentName: doc.fileName ?? doc.name,
        markdownLink: `[${doc.fileName ?? doc.name}](${presignedUrl})`,
      };
    });
  });
